import { FC, useCallback, useEffect, useMemo } from "react";
import { Grid } from "semantic-ui-react";
import { SearchValue } from "../../../../components/SearchValue/SearchValue";

interface Product {
  productId: string;
  productName: string;
  salesUnits: Array<{ salesUnitId: string; salesUnitName: string }>;
}

interface OwnProductFieldsProps {
  productName: string;
  setProductName: (value: string) => void;
  unitSize: string;
  setUnitSize: (value: string) => void;
  products?: Product[];
  selectedSalesUnitIds: string[];
}

interface ProductUnitOption {
  key: string;
  title: string;
  value: string;
  productId: string;
  salesUnitId: string;
  disabled: boolean;
}

const OwnProductFields: FC<OwnProductFieldsProps> = ({
  productName,
  setProductName,
  unitSize,
  setUnitSize,
  products = [],
  selectedSalesUnitIds = []
}) => {
  const createCompositeKey = useCallback((productId: string, salesUnitId: string): string => {
    return `${productId}-${salesUnitId}`;
  }, []);

  const parseCompositeKey = useCallback((compositeKey: string): [string, string] | null => {
    const parts = compositeKey.split("-");
    return parts.length === 2 && parts[0] && parts[1] ? [parts[0], parts[1]] : null;
  }, []);

  const currentSelectionKey = useMemo(
    () => (productName && unitSize ? createCompositeKey(productName, unitSize) : ""),
    [productName, unitSize, createCompositeKey]
  );

  const allProductUnitOptions = useMemo<ProductUnitOption[]>(() => {
    const uniqueProducts = Array.from(
      new Map(products.map(product => [product.productId, product])).values()
    );
    const optionsMap = new Map<string, ProductUnitOption>();
    uniqueProducts.forEach(product => {
      product.salesUnits?.forEach(unit => {
        const compositeKey = createCompositeKey(product.productId, unit.salesUnitId);

        if (!optionsMap.has(compositeKey)) {
          optionsMap.set(compositeKey, {
            key: compositeKey,
            title: `${product.productName} - ${unit.salesUnitName}`,
            value: compositeKey,
            productId: product.productId,
            salesUnitId: unit.salesUnitId,
            disabled: selectedSalesUnitIds.includes(unit.salesUnitId)
          });
        }
      });
    });
    return Array.from(optionsMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [products, selectedSalesUnitIds, createCompositeKey]);

  const availableOptions = useMemo(() => {
    return allProductUnitOptions.filter(
      option => !option.disabled || option.value === currentSelectionKey
    );
  }, [allProductUnitOptions, currentSelectionKey]);

  const searchableOptions = useMemo(
    () =>
      availableOptions.map(option => ({
        id: option.value,
        title: option.title,
        disabled: option.disabled
      })),
    [availableOptions]
  );

  const selectedOptionDisplayText = useMemo(() => {
    if (!currentSelectionKey) return "";
    return allProductUnitOptions.find(option => option.value === currentSelectionKey)?.title || "";
  }, [allProductUnitOptions, currentSelectionKey]);

  useEffect(() => {
    if (
      currentSelectionKey &&
      !allProductUnitOptions.some(option => option.value === currentSelectionKey)
    ) {
      setProductName("");
      setUnitSize("");
    }
  }, [allProductUnitOptions, currentSelectionKey, setProductName, setUnitSize]);

  const handleProductUnitSelection = useCallback(
    (data: { result?: { id?: string } }) => {
      const selectedValue = data?.result?.id;
      if (!selectedValue) return;

      const parsedKey = parseCompositeKey(selectedValue);
      if (parsedKey) {
        const [selectedProductId, selectedSalesUnitId] = parsedKey;
        setProductName(selectedProductId);
        setUnitSize(selectedSalesUnitId);
      }
    },
    [parseCompositeKey, setProductName, setUnitSize]
  );

  return (
    <Grid.Column computer={8} tablet={8} mobile={16}>
      <SearchValue
        title="Product and Unit Size"
        placeholder="Type to search products & unit sizes"
        defaultValue={selectedOptionDisplayText}
        searchDataValues={searchableOptions}
        selectDetails={handleProductUnitSelection}
        checkOther={() => {}}
        noResultEnable
        noResultData={{
          id: -1,
          title: "No matching product or unit found",
          disabled: true
        }}
        disable={false}
      />
    </Grid.Column>
  );
};

export default OwnProductFields;
